import { FormComponent, FormComponentType } from "@components/FormBuilder/types"
import { ContractDto } from "@custom-types/contract"
import { GenericForm } from "@custom-types/form"
import { GridRowId, GridValidRowModel } from "@mui/x-data-grid"
import { GridApiCommunity } from "@mui/x-data-grid/internals"

export const getUserFormFields = (options: FormComponent["options"]): GenericForm => ({
  first_name: {
    title: "Name",
    editable: true,
    type: FormComponentType.Input,
  },
  last_name: {
    title: "Surname",
    editable: true,
    type: FormComponentType.Input,
  },
  organisation_key: {
    title: "Organisation",
    editable: false,
    type: FormComponentType.Dropdown,
    options,
  },
  key: {
    title: "Email",
    editable: false,
    type: FormComponentType.Input,
  },
  last_updated: {
    title: "Last Updated",
    editable: false,
    type: FormComponentType.Input,
  },
  last_login: {
    title: "Last Login",
    editable: false,
    type: FormComponentType.Input,
  },
  active: {
    title: "Active",
    editable: true,
    type: FormComponentType.Checkbox,
  },
})

const getUsedContractRolesMap = (rows: GridValidRowModel[], groupBy: "contract" | "role") => {
  const valueField = groupBy === "contract" ? "role" : "contract"

  return rows.reduce<Record<string, string[]>>((result, row) => {
    if (!result[row[groupBy]]) result[row[groupBy]] = []
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    if (!result[row[groupBy]].includes(row[valueField])) result[row[groupBy]].push(row[valueField])

    return result
  }, {})
}

// Selecting the same combination of contract + role is forbidden.
// This function will return array of contracts with info if is already used.
export const getAvailableContracts = (
  contracts: ContractDto[],
  gridApi: GridApiCommunity,
  rowId: GridRowId,
): { contract: ContractDto; isUsed: boolean }[] => {
  const rows = gridApi.getAllRowIds().map((id) => gridApi.getRowWithUpdatedValues(id, "contract"))
  const currentRow = rows.find(({ id }) => id === rowId)
  const usedContractRoles = getUsedContractRolesMap(rows, "contract")

  if (!currentRow) return []

  return contracts.map((contract) => {
    const isCurrentOption = currentRow.contract === contract.key
    const usedRoles = usedContractRoles[contract.key]

    if (!usedRoles || isCurrentOption) return { contract, isUsed: false }

    return { contract, isUsed: usedRoles.includes(currentRow.role) }
  })
}

const roleOptions = [
  { value: "whatif", label: "Whatif" },
  { value: "support", label: "Support" },
  { value: "viewer", label: "Viewer" },
  { value: "build", label: "Build" },
  { value: "template", label: "Template" },
]

// Selecting the same combination of contract + role is forbidden.
// This function will return array of role options with info if is already used.
export const getAvailableContractRoles = (
  gridApi: GridApiCommunity,
  rowId: GridRowId,
): { value: string; label: string; isUsed: boolean }[] => {
  const rows = gridApi.getAllRowIds().map((id) => gridApi.getRowWithUpdatedValues(id, "contract"))
  const currentRow = rows.find(({ id }) => id === rowId)
  const usedContractRoles = getUsedContractRolesMap(rows, "role")

  if (!currentRow) return []

  return roleOptions.map(({ value, label }) => {
    const isCurrentOption = currentRow.role === value
    const usedContracts = usedContractRoles[value]

    if (!usedContracts || isCurrentOption) return { value, label, isUsed: false }

    return { value, label, isUsed: usedContracts.includes(currentRow.contract) }
  })
}
